import React, { lazy, Suspense, useCallback, useEffect } from "react";
import { Router, Switch, Route, Redirect, useHistory, useLocation } from "react-router-dom";
import { createBrowserHistory } from "history";
import * as Sentry from "@sentry/react";

import DashboardLayout from "../components/dashboardLayout/DashboardLayout";
import LinkFirstWhatsapp from "../components/simplifiedOnboarding/LinkFirstWhatsapp.component";
import SuccessfulLink from "../components/simplifiedOnboarding/successfullLink.component";
import WhatsappLinkInstructions from "../components/simplifiedOnboarding/WhatsappLinkInstructions.component";
import WhatsappLinkProfile from "../components/simplifiedOnboarding/WhatsappLinkProfile.component";
import WhatsappLinkQrCode from "../components/simplifiedOnboarding/WhatsappLinkQrCode.component";
import CONSTANTS from "../const/constants";
import AnalyticsView from "../views/dashboard/AnalyticsView/AnalyticsView";
import BroadcastView from "../views/dashboard/BroadcastView/BroadcastView";

import SplashScreen from "../views/dashboard/SplashScreen";
import PanelView from "../views/panel/panel.view";
import Multimedia from "../views/settings/multimedia/Multimedia.component";
import OnboardingUseOldNumber from "../components/simplifiedOnboarding/OnboardingUseOldNumber.component";
import WhatsappLinkQrCodeInstructions from "../components/simplifiedOnboarding/WhatsappLinkQrCodeInstructions.component";
import TwoFactorQrCodeView from "../views/twoFactor/TwoFactorQrCode";
import TwoFactorView from "../views/twoFactor/TwoFactor";
import SelectPlanView from "../views/select-plan/SelectPlanView";
import { Provider } from "../components/dnd-kit/context";
import { AppState } from "../components/dnd-kit/model";
import { getSedeModeRedirect } from "./routeGuard";
import { isSedeMode } from "../utils/partnerAccess/partnerStorage";
import { PARTNER_OVERVIEW_ROUTE } from "../views/partnerOverview/navigation";
import { installNextZoneGuard } from "../utils/zoneNavigation";
import LandingRedirect from "../views/dashboard/inicio/components/landing-redirect";
import { isHumanTasksFixturesMode } from "../views/dashboard/inicio/helpers/fixtures-mode";

const SentryRoute = Sentry.withSentryRouting(Route);

const history = createBrowserHistory();

// A path served by the other zone has no route here: reaching one from inside
// this app reloads the page instead of leaving a blank screen.
installNextZoneGuard(history);

export { history };

const InitialPayment = lazy(() =>
  import("../components/simplifiedOnboarding/InitialPayment.component")
);
const LoginView = lazy(() => import("../views/login/login.view"));
const ChatView = lazy(() => import("../views/dashboard/ChatView"));
const Boards = lazy(() => import("../views/Boards"));
const Board = lazy(() => import("../views/Board"));
const CalendlyAuth = lazy(() => import("../views/settings/integration/CalendlyAuth.component"));
const QuickAnswersView = lazy(() =>
  import("../views/dashboard/QuickAnswers/QuickAnswers.view")
);
const QuickAnswersAction = lazy(() =>
  import(
    "../views/dashboard/QuickAnswers/QuickAnswersAction/QuickAnswersAction.view"
  )
);
const Users = lazy(() => import("../views/users/Users.component"));
const UsersAction = lazy(() =>
  import("../views/users/usersAction/UsersAction.component")
);
const Spaces = lazy(() => import("../views/spaces/Spaces.view"));
const SpacesAction = lazy(() =>
  import("../views/spaces/spacesAction/SpacesAction.component")
);
const LinkWhatsapp = lazy(() =>
  import("../views/spaces/linkWhatsapp/LinkWhatsapp.component")
);
const Setting = lazy(() => import("../views/settings/Settings.view"));
const Bot = lazy(() => import("../views/BotView"));
const TagsAction = lazy(() =>
  import("../views/settings/tags/tagsAction/TagsAction.view")
);
const WhatsappTemplatesAction = lazy(() =>
  import(
    "../views/settings/whatsappTemplates/whatsappTemplatesAction/WhatsappTemplatesAction.view"
  )
);
const analyticsWhitAgents = lazy(() =>
  import("../views/dashboard/AnalyticsView/AnaliticsWithAgents")
);
const RolesAction = lazy(() =>
  import("../views/settings/roles/rolesAction/RolesAction.view")
);
const ForbiddenView = lazy(() => import("../views/forbidden/forbidden.view"));
const MaintenanceView = lazy(() => import("../views/maintenance/maintenance.view"));
const RecoverPasswordView = lazy(() =>
  import("../views/recover-password/RecoverPassword.view")
);
const ChangePasswordView = lazy(() =>
  import("../views/change-password/ChangePassword.view")
);
const MagicLinkView = lazy(() =>
  import("../views/magic-link/MagicLink.view")
);
const SSOErrorView = lazy(() =>
  import("../views/sso-error/SSOError.view")
);
const DatasheetConfigurationAction = lazy(() =>
  import(
    "../views/settings/dataSheetConfiguration/dataSheetConfigurationAction/DatasheetConfigurationAction.component"
  )
);
const Album = lazy(() =>
  import("../views/settings/multimedia/album/Album.component")
);
const AccountView = lazy(() => import("../views/settings/Account.view"));
const ReferralCodesView = lazy(() =>
  import("../views/referral-codes/ReferralCodes.view")
);
const Contacts = lazy(() => import("../views/contacts/Contacts.component"));
const Help = lazy(() => import("../views/help/Help"));
const DashboardView = lazy(() =>
  import("../views/dashboard/DashboardView/DashboardView")
);
const TemplateAnalysisView = lazy(() =>
  import("../views/dashboard/TemplateAnalysisView/TemplateAnalysisView")
);
const CampaignAnalyticsView = lazy(() =>
  import("../views/dashboard/campaign-analytics/views/campaign-analytics-view")
);
const InicioView = lazy(() => import("../views/dashboard/inicio/views/inicio-view"));
const InicioPreview = lazy(() => import("../views/dashboard/inicio/components/inicio-preview"));

const SimplifiedOnboarding = lazy(() =>
  import("../components/simplifiedOnboarding/SimplifiedOnboarding.component")
);
const PartnerOverview = lazy(() => import("../views/partnerOverview"));

// Partner access: a sede tab only works the chat. Every route is decided
// here, the ones rendered outside `DashboardLayout` included, and a screen
// that is not allowed is never mounted. Outside sede mode it is transparent.
//
// A location blocked at mount is replaced with the chat. Later in-app
// navigations are refused BEFORE they commit (`history.block`): a redirect
// then would swap the <Switch> for a <Redirect> and remount the chat.
const sedeModeRedirectFor = (path) => {
  const sedeMode = isSedeMode();
  return getSedeModeRedirect({
    path,
    sedeMode,
    authenticated: sedeMode && Boolean(localStorage.getItem("token")),
  });
};

const SedeRouteGuard = ({ children }) => {
  const history = useHistory();
  const location = useLocation();
  const sedeMode = isSedeMode();

  useEffect(() => {
    if (!sedeMode) return undefined;
    // Entering or leaving a sede always reloads the page, so this is the
    // whole life of the tab as a sede. `false` cancels the transition.
    return history.block((next) => (sedeModeRedirectFor(next.pathname) ? false : undefined));
  }, [history, sedeMode]);

  const redirectTo = sedeModeRedirectFor(location.pathname);
  return redirectTo ? <Redirect to={redirectTo} /> : children;
};

const Routes = () => (
  <Suspense fallback={<SplashScreen />}>
    <Provider initialState={new AppState()}>
      <Router history={history}>
        <SedeRouteGuard>
        <Switch>
          <SentryRoute
            path="/onboarding/step/:currentStep"
            exact
            render={() => <SimplifiedOnboarding />}
          />
          <SentryRoute
            path="/select-plan"
            exact
            render={() => <SelectPlanView />}
          />
          <SentryRoute
            path={CONSTANTS.FIRST_PAYMENT_ROUTE}
            exact
            render={() => <InitialPayment />}
          />
          <SentryRoute
            path="/phone-selection"
            exact
            render={() => <OnboardingUseOldNumber />}
          />
          <SentryRoute
            path="/qr-code"
            exact
            render={() => <TwoFactorQrCodeView />}
          />
          <SentryRoute
            path="/two-factor"
            exact
            render={() => <TwoFactorView />}
          />
          <SentryRoute
            path="/link-whatsapp"
            exact
            render={() => <LinkFirstWhatsapp />}
          />
          <SentryRoute
            path="/link-whatsapp/whatsappLink/instructions"
            exact
            render={() => <WhatsappLinkInstructions />}
          />
          <SentryRoute
            path="/link-whatsapp/profile"
            exact
            render={() => <WhatsappLinkProfile />}
          />
          <SentryRoute
            path="/select-plan"
            exact
            render={() => <SelectPlanView />}
          />
          <SentryRoute
            path={CONSTANTS.FIRST_PAYMENT_ROUTE}
            exact
            render={() => <InitialPayment />}
          />
          <SentryRoute
            path="/phone-selection"
            exact
            render={() => <OnboardingUseOldNumber />}
          />
          <SentryRoute
            path="/qr-code"
            exact
            render={() => <TwoFactorQrCodeView />}
          />
          <SentryRoute
            path="/two-factor"
            exact
            render={() => <TwoFactorView />}
          />
          <SentryRoute
            path="/link-whatsapp"
            exact
            render={() => <LinkFirstWhatsapp />}
          />
          <SentryRoute
            path="/link-whatsapp/whatsappLink/instructions"
            exact
            render={() => <WhatsappLinkInstructions />}
          />
          <SentryRoute
            path="/link-whatsapp/profile"
            exact
            render={() => <WhatsappLinkProfile />}
          />
          <SentryRoute
            path="/link-whatsapp/qrcode-instructions"
            exact
            render={() => <WhatsappLinkQrCodeInstructions />}
          />
          <SentryRoute
            path="/link-whatsapp/qrcode"
            exact
            render={() => <WhatsappLinkQrCode />}
          />
          <SentryRoute
            path="/success-link"
            exact
            render={() => <SuccessfulLink />}
          />
          {/* Who receives tasks lands on Inicio; everybody else on the chat. */}
          <SentryRoute exact path="/">
            <LandingRedirect />
          </SentryRoute>
          <SentryRoute path="/login" exact render={() => <LoginView />} />
          {/* Example-data mode only: Inicio without login, for local visual review. */}
          {isHumanTasksFixturesMode() && (
            <SentryRoute path="/inicio-preview" exact render={() => <InicioPreview />} />
          )}
          <SentryRoute
            path="/auth/magic"
            exact
            render={() => <MagicLinkView />}
          />
          <SentryRoute
            path="/auth/sso/error"
            exact
            render={() => <SSOErrorView />}
          />

          <SentryRoute path="/integrations/calendly_auth" exact render={() => <CalendlyAuth />} />
          <SentryRoute
            path="/forbidden"
            exact
            render={() => <ForbiddenView />}
          />
          <SentryRoute
            path="/maintenance"
            exact
            render={() => <MaintenanceView />}
          />
          <SentryRoute
            path="/recoverPassword"
            exact
            render={() => <RecoverPasswordView />}
          />
          <SentryRoute
            path="/changePassword"
            exact
            render={() => <ChangePasswordView />}
          />
          <SentryRoute
            exact
            validateAuth
            path={CONSTANTS.LINK_WHATSAPP_ROUTE}
            component={LinkWhatsapp}
          />
          <SentryRoute
            path={`${CONSTANTS.REF_CODE_ROUTE}/:refCode`}
            component={ReferralCodesView}
          />
          {/* Partner access: the overview of all the sedes has its own header
              and no module nav, so it is rendered outside the layout and
              checks the session itself. */}
          <SentryRoute
            exact
            path={PARTNER_OVERVIEW_ROUTE}
            render={() => <PartnerOverview />}
          />
          <DashboardLayout
            exact
            validateAuth
            path="/chat"
            component={ChatView}
          />
           <DashboardLayout
            exact
            validateAuth
            path="/chat/:chatId?"
            component={ChatView}
          />
          <DashboardLayout
            exact
            validateAuth
            path={CONSTANTS.QUICK_ANSWERS_ROUTE}
            component={QuickAnswersView}
          />
          <DashboardLayout
            exact
            validateAuth
            path={CONSTANTS.QUICK_ANSWERS_ACTION_ROUTE}
            component={QuickAnswersAction}
          />
          <DashboardLayout
            exact
            validateAuth
            path={CONSTANTS.MULTIMEDIA_ROUTE}
            component={Multimedia}
          />
          <DashboardLayout
            exact
            validateAuth
            path={CONSTANTS.ALBUM_ROUTE}
            component={Album}
          />
          <DashboardLayout
            exact
            validateAuth
            path={CONSTANTS.SUBALBUM_ROUTE}
            component={Album}
          />
          <DashboardLayout exact validateAuth path="/users" component={Users} />
          <DashboardLayout
            exact
            validateAuth
            path="/users/new"
            component={UsersAction}
          />
          <DashboardLayout
            exact
            validateAuth
            path="/spaces"
            component={Spaces}
          />
          <DashboardLayout
            exact
            validateAuth
            path="/spaces/new"
            component={SpacesAction}
          />
          <DashboardLayout
            exact
            validateAuth
            path="/spaces/new-whatsapp"
            component={LinkWhatsapp}
          />
          <DashboardLayout
            exact
            validateAuth
            path="/contacts"
            component={Contacts}
          />
          <DashboardLayout
            exact
            validateAuth
            path="/config"
            component={Setting}
          />
          <DashboardLayout exact validateAuth path="/help" component={Help} />
          <DashboardLayout exact validateAuth path="/bot" component={Bot} />
          <DashboardLayout
            exact
            validateAuth
            path="/config/new-tag"
            component={TagsAction}
          />
          <DashboardLayout
            exact
            validateAuth
            path="/config/new-whatsapp-template"
            component={WhatsappTemplatesAction}
          />
          <DashboardLayout
            exact
            validateAuth
            path="/config/new-rol"
            component={RolesAction}
          />
          <DashboardLayout
            exact
            validateAuth
            path="/config/new-datasheet"
            component={DatasheetConfigurationAction}
          />
          <DashboardLayout
            exact
            validateAuth
            path={CONSTANTS.UPDATE_SUBSCRIPTION_ROUTE}
            component={AccountView}
          />
          <DashboardLayout
            exact
            validateAuth
            path="/panel"
            component={PanelView}
          />
          <DashboardLayout
            exact
            validateAuth
            path="/analytics"
            component={AnalyticsView}
          />
          <DashboardLayout
            exact
            validateAuth
            path="/analytics/agents"
            component={analyticsWhitAgents}
          />
          <DashboardLayout
            exact
            validateAuth
            path="/broadcast/:currentStep"
            component={BroadcastView}
          />
          <DashboardLayout
            exact
            validateAuth
            path="/broadcast/analytics/:campaignId"
            component={CampaignAnalyticsView}
          />
          <DashboardLayout
            exact
            validateAuth
            path="/boards"
            component={Boards}
          />
          <DashboardLayout
            exact
            validateAuth
            path="/boards/:id"
            component={Board}
          />
          <DashboardLayout
            exact
            validateAuth
            path="/inicio"
            component={InicioView}
          />
          <DashboardLayout
            exact
            validateAuth
            path="/dashboard"
            component={DashboardView}
          />
          <DashboardLayout
            exact
            validateAuth
            path="/plantillas"
            component={TemplateAnalysisView}
          />
        </Switch>
        </SedeRouteGuard>
      </Router>
    </Provider>
  </Suspense>
);

export default Routes;
